import { type UseGoogleMapViewProps } from 'api/google-api/hooks/useGoogleMapView';
import { getCharityVettingFlowStatus } from 'features/charity-vetting/utils/storageHandlers';

import {
  type CharityVettingFormValues,
  type PliType,
  type CountryCode,
  CountryCodeEnum,
} from '../types/charityVettingTypes';

export const PREFERRED_COUNTRIES: CountryCode[] = [
  CountryCodeEnum.GB,
  CountryCodeEnum.PT,
  CountryCodeEnum.ES,
  CountryCodeEnum.PL,
  CountryCodeEnum.FR,
];
export const COMMON_COUNTRIES: CountryCode[] = [
  CountryCodeEnum.PT,
  CountryCodeEnum.ES,
  CountryCodeEnum.PL,
  CountryCodeEnum.FR,
];

export const INITIAL_PLI_OBJECT: PliType = {
  code: 'GBP',
  minorUnits: 0,
  decimals: 2,
  value: undefined,
};

export const INITIAL_COORDS: UseGoogleMapViewProps['coords'] = {
  latitude: 51.509865,
  longitude: -0.118092,
};

export const setDefaultCoordinates = (coords: UseGoogleMapViewProps['coords']) =>
  coords?.latitude && coords?.longitude ? coords : INITIAL_COORDS;

export const initialValues: CharityVettingFormValues = {
  charityOnboarding: {
    search: '',
    charityName: '',
    email: '',
    address: '',
    extraAddressLine: '',
    postalCode: '',
    city: '',
    phoneNumber: '',
    countryIsoCode: null,
    pointOfContactName: '',
    isLegalRepresentative: undefined,
    organizationDescription: '',
    socialMediaAccountLinks: [
      { type: 'facebook', link: '' },
      { type: 'instagram', link: '' },
      { type: 'linkedin', link: '' },
    ],
    acceptsAllFood: undefined,
    unwantedFoodItems: [],
    additionalUnwantedFoodCategories: [],
    manualFormSelected: false,
    numberOfUnwantedFoodItems: 0,
    gmAddressCoordinates: setDefaultCoordinates(INITIAL_COORDS),
    charityType: null,
    // UK
    charityRegistrationId: '',
    isCompany: undefined,
    companyRegistrationNumber: '',
    isFsaRated: undefined,
    hasHygieneCertificate: undefined,
    hasPli: undefined,
    pliCover: { ...INITIAL_PLI_OBJECT },
    // PT
    nif: undefined,
    nipc: undefined,
    fiscalBenefitsArticle: undefined,
    donationPreferences: undefined,
    preparationKitchen: undefined,
    consumptionType: undefined,
    haccp: undefined,
    // ES
    cif: undefined,
    donationCertificate: undefined,
    hasRgseaa: undefined,
    rgseaa: undefined,
    // PL
    krs: undefined,
    nip: undefined,
    isPublicBenefitOrg: undefined,
    // FR
    isAuthorisedFoodAid: undefined,
    organisationPurpose: undefined,
    organisationSpecificType: undefined,
    decreePublicationDate: undefined,
    officialJournalPublicationDate: undefined,
    arretePublicationDate: undefined,
    organisationSituation: undefined,
    organisationOtherOption: undefined,
    agrementDate: undefined,
    siren: undefined,
    rna: undefined,
    // Food Safety
    staffTrainedInFoodHygiene: undefined,
    hasFoodSafetyManagementPlan: undefined,
    hasHaccpProcedures: undefined,
    vettingDocuments: undefined,
  },
  flowManagement: {
    flowProgress: {
      GB: {
        'get-started': false,
        search: false,
        'authorised-food-aid': false,
        registration: false,
        'donation-preferences': false,
        'social-accounts': false,
        'review-details': false,
        'decree-publication-dates': false,
        'arrete-publication-date': false,
        'organisation-situation': false,
        'organisation-other-option': false,
        'agrement-date': false,
      },
      PT: {
        'get-started': false,
        search: false,
        'authorised-food-aid': false,
        registration: false,
        'donation-preferences': false,
        'social-accounts': false,
        'review-details': false,
        'decree-publication-dates': false,
        'arrete-publication-date': false,
        'organisation-situation': false,
        'organisation-other-option': false,
        'agrement-date': false,
      },
      ES: {
        'get-started': false,
        search: false,
        'authorised-food-aid': false,
        registration: false,
        'donation-preferences': false,
        'social-accounts': false,
        'review-details': false,
        'decree-publication-dates': false,
        'arrete-publication-date': false,
        'organisation-situation': false,
        'organisation-other-option': false,
        'agrement-date': false,
      },
      PL: {
        'get-started': false,
        search: false,
        'authorised-food-aid': false,
        registration: false,
        'donation-preferences': false,
        'social-accounts': false,
        'review-details': false,
        'decree-publication-dates': false,
        'arrete-publication-date': false,
        'organisation-situation': false,
        'organisation-other-option': false,
        'agrement-date': false,
      },
      FR: {
        'get-started': false,
        search: false,
        'authorised-food-aid': false,
        registration: false,
        'donation-preferences': false,
        'food-safety-question-three': false,
        'association-registration': false,
        'social-accounts': false,
        'review-details': false,
        'decree-publication-dates': false,
        'arrete-publication-date': false,
        'organisation-situation': false,
        'organisation-other-option': false,
        'agrement-date': false,
      },
    },
  },
};

/**
 * The default form shape plus the persisted per-country flow progress. It does
 * NOT read the saved form-values carrier — CharityVettingFormProvider owns the
 * defaults ← server ← carrier layering (and the `countryIsoCode` it resolves),
 * so the carrier is applied in exactly one place.
 */
export const handleInitialValues = (country: CountryCode | undefined): CharityVettingFormValues => {
  const flowStatusFromStorage = getCharityVettingFlowStatus();
  const localisedCountry = country ?? CountryCodeEnum.GB;

  return {
    charityOnboarding: {
      ...initialValues.charityOnboarding,
    },
    flowManagement: {
      flowProgress: {
        ...initialValues.flowManagement.flowProgress,
        ...(flowStatusFromStorage ? { [localisedCountry]: { ...flowStatusFromStorage } } : {}),
      },
    },
  };
};
